import { SearchOutlined } from '@ant-design/icons';
import { AutoComplete, Button, Input, List } from 'antd';
import cx from 'classnames';
import debounce from 'lodash.debounce';
import React, { useCallback, useMemo, useState } from 'react';
import { NavigationListItem } from '../../../model/CommonModel';
import styles from './navigationList.module.scss';

type Props = {
	idPrefix: string;
	dataSource?: NavigationListItem[];
	selectedItemId: number;
	onElementClicked(item: NavigationListItem);
	setSelectedItemId: (id: number) => void;
};

const NavigationList: React.FC<Props> = ({ idPrefix, dataSource, selectedItemId, onElementClicked, setSelectedItemId }) => {
	//#region state management variables
	const [searchString, setSearchString] = useState<string>('');
	const [inputValue, setInputValue] = useState<string>('');
	const [isSearchActive, setIsSearchActive] = useState<boolean>(false);
	const filteredDataSource = useMemo(() => {
		if (!isSearchActive) {
			return dataSource;
		} else {
			return dataSource.filter(p => p.Name.toLowerCase().includes(searchString.toLocaleLowerCase()));
		}
	}, [isSearchActive, searchString, dataSource]);
	//#endregion

	const handleSearch = useCallback(
		debounce(async (value: string) => {
			setSearchString(value.trim());
			if (!value.trim()) {
				setSearchString('');
				setIsSearchActive(false);
				if (!selectedItemId) {
					setSelectedItemId(dataSource[0].Id as number);
				}
			} else {
				setIsSearchActive(true);
				const filteredSearchResults = dataSource.filter(p => p.Name.toLowerCase().includes(value.toLocaleLowerCase()));
				setSelectedItemId((filteredSearchResults.length ? filteredSearchResults[0].Id : 0) as number);
			}
		}, 500),
		[selectedItemId, searchString]
	);

	const handleOnKeyDown = e => {
		if (searchString && e.keyCode === 13) {
			handleSearch(e.target.value);
		}
	};

	const handleOnChangeAutocomplete = (value: string) => {
		if (value && value.length >= 100) {
			value = value.substring(0, 100);
		}
		setInputValue(value);
	};

	return (
		<>
			<div className={styles.container}>
				<div className={styles.searchContainer}>
					<AutoComplete
						allowClear={true}
						onSearch={handleSearch}
						placeholder={_('SearchByName')}
						onKeyUp={handleOnKeyDown}
						value={inputValue}
						onChange={handleOnChangeAutocomplete}
					/>
					{false && (
						<Input
							id={`${idPrefix}SearchInput`}
							className={styles.searchInput}
							value={searchString}
							placeholder={_('SearchByName')}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
								handleSearch(e.target.value);
							}}
							onPressEnter={(e: React.KeyboardEvent<HTMLInputElement>) => {
								if (searchString && (e.key === 'Enter' || e.key === 'Return')) {
									handleSearch(searchString);
								}
							}}
						/>
					)}
					<Button
						id={`${idPrefix}SearchButton`}
						className={styles.searchButton}
						type={isSearchActive ? 'primary' : 'default'}
						disabled={!searchString}
						onClick={() => handleSearch(searchString)}>
						<SearchOutlined className={styles.searchIcon} />
					</Button>
				</div>
				<List
					id={`${idPrefix}List`}
					className={styles.menuList}
					dataSource={filteredDataSource}
					renderItem={item => (
						<List.Item
							className={cx(styles.menuListItem, {
								[styles.menuListItemSelected]: item.Id === selectedItemId,
							})}
							onClick={() => onElementClicked(item)}>
							<span id={`${idPrefix}Item${item.Id}`} className={styles.spanStyle}>
								{_(item.Name)}
							</span>
						</List.Item>
					)}
				/>
			</div>
		</>
	);
};

export { NavigationList };
